/**
 * reportExportUtils.js
 * Generates standalone, print-ready, beautifully styled HTML reports
 * for Job Report, Client Report, and Submission Report with full filter reflection.
 */

// Helper to trigger browser file download from HTML string
export function triggerHtmlDownload(htmlContent, fileName) {
  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Common CSS styles embedded in all downloaded reports
const commonCss = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    background: #f8fafc;
    color: #1e293b;
    padding: 32px;
    line-height: 1.5;
  }
  .report-container {
    max-width: 1100px;
    margin: 0 auto;
    background: #ffffff;
    border-radius: 12px;
    padding: 32px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
    border: 1px solid #e2e8f0;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 2px solid #009688;
    padding-bottom: 20px;
    margin-bottom: 24px;
  }
  .brand-title {
    font-size: 24px;
    font-weight: 800;
    color: #0f172a;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .brand-title span {
    color: #009688;
  }
  .meta-info {
    font-size: 13px;
    color: #64748b;
    margin-top: 4px;
  }
  .download-pdf-btn, .print-btn {
    background: #009688;
    color: #ffffff;
    border: none;
    padding: 8px 18px;
    border-radius: 6px;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    transition: background 0.2s, opacity 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .download-pdf-btn:hover, .print-btn:hover {
    background: #0f766e;
  }
  .filters-banner {
    background: #f1f5f9;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .filter-label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #475569;
  }
  .badge {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .badge.date    { background: #ffffff; color: #1e293b; border: 1px solid #cbd5e1; }
  .badge.region  { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
  .badge.source  { background: #faf5ff; color: #7e22ce; border: 1px solid #e9d5ff; }
  .badge.client  { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
  .badge.status  { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
  
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 32px;
  }
  .kpi-card {
    background: #ffffff;
    border-radius: 10px;
    padding: 18px 20px;
    border: 1px solid #e2e8f0;
    border-top: 4px solid #cbd5e1;
  }
  .kpi-card.teal   { border-top-color: #009688; background: #f0fdfb; }
  .kpi-card.blue   { border-top-color: #3b82f6; background: #eff6ff; }
  .kpi-card.green  { border-top-color: #10b981; background: #ecfdf5; }
  .kpi-card.red    { border-top-color: #ef4444; background: #fef2f2; }
  .kpi-card.amber  { border-top-color: #f59e0b; background: #fffbeb; }
  .kpi-card.purple { border-top-color: #8b5cf6; background: #faf5ff; }
  
  .kpi-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }
  .kpi-card.teal   .kpi-title { color: #0f766e; }
  .kpi-card.blue   .kpi-title { color: #1d4ed8; }
  .kpi-card.green  .kpi-title { color: #047857; }
  .kpi-card.red    .kpi-title { color: #b91c1c; }
  .kpi-card.amber  .kpi-title { color: #b45309; }
  .kpi-card.purple .kpi-title { color: #6b21a8; }
  
  .kpi-value {
    font-size: 26px;
    font-weight: 800;
    color: #0f172a;
  }
  .kpi-sub {
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
    font-weight: 500;
  }
  
  .section-title {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 12px;
    border-left: 4px solid #009688;
    padding-left: 10px;
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 32px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
  }
  thead th {
    background: #f8fafc;
    padding: 11px 14px;
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    color: #64748b;
    font-weight: 700;
    border-bottom: 1px solid #e2e8f0;
  }
  tbody td {
    padding: 11px 14px;
    font-size: 13px;
    color: #334155;
  }
  tbody tr {
    border-bottom: 1px solid #f1f5f9;
  }
  tbody tr:nth-child(even) {
    background: #fafafa;
  }
  
  .status-tag {
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 700;
    display: inline-block;
  }
  .status-tag.active  { background: #ecfdf5; color: #059669; }
  .status-tag.missing { background: #fef2f2; color: #dc2626; }
  
  .footer {
    margin-top: 36px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
    font-size: 12px;
    color: #94a3b8;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  @media print {
    body { padding: 0; background: #ffffff; }
    .report-container { box-shadow: none; padding: 0; border: none; }
    .download-pdf-btn, .print-btn { display: none !important; }
  }
`;

const pdfScriptTag = `
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
  <script>
    function downloadAsPDF() {
      var btn = document.getElementById('downloadPdfBtn') || document.querySelector('.download-pdf-btn');
      var originalHtml = btn ? btn.innerHTML : '';
      if (btn) {
        btn.innerHTML = '⏳ Generating PDF...';
        btn.disabled = true;
        btn.style.opacity = '0.7';
      }

      var element = document.querySelector('.report-container') || document.body;
      var cleanTitle = (document.title || 'Report')
        .replace(/[^a-zA-Z0-9_\\-\\s]/g, '')
        .trim()
        .replace(/\\s+/g, '_');

      var opt = {
        margin: [8, 8, 8, 8],
        filename: cleanTitle + '.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      if (window.html2pdf) {
        if (btn) btn.style.display = 'none';
        html2pdf().set(opt).from(element).save().then(function() {
          if (btn) {
            btn.style.display = '';
            btn.innerHTML = originalHtml;
            btn.disabled = false;
            btn.style.opacity = '1';
          }
        }).catch(function(err) {
          console.error('PDF error:', err);
          if (btn) {
            btn.style.display = '';
            btn.innerHTML = originalHtml;
            btn.disabled = false;
            btn.style.opacity = '1';
          }
          window.print();
        });
      } else {
        window.print();
        if (btn) {
          btn.innerHTML = originalHtml;
          btn.disabled = false;
          btn.style.opacity = '1';
        }
      }
    }
  </script>
`;

// ─────────────────────────────────────────────────────────────────────────────
// 1. JOB REPORT (JobCountReport.js) HTML Generator
// ─────────────────────────────────────────────────────────────────────────────
export function generateJobReportHtml({
  startDate,
  endDate,
  regionFilter,
  creationType,
  selectedClient,
  clientSubFilter,
  displayedTotals = {},
  filteredClientBreakdown = [],
  workModeStats = {},
  byWorkMode = []
}) {
  const regionLabel = regionFilter === 'offshore' ? 'Offshore' : 'Onsite';
  const creationLabel = creationType === 'automation' ? 'Automation' : creationType === 'manual' ? 'Manual' : 'All Sources';
  const clientLabel = selectedClient || 'All Clients';
  const subFilterLabel = clientSubFilter === 'with_subs' ? 'With Submissions' : clientSubFilter === 'without_subs' ? 'Missing Submissions' : 'All Roles';
  const dateRange = `${startDate || 'N/A'} — ${endDate || 'N/A'}`;

  const totalJobs = displayedTotals.jobCount ?? 0;
  const totalSubs = displayedTotals.submissionCount ?? 0;
  const jobsWithSubs = displayedTotals.jobsWithSubs ?? 0;
  const jobsWithoutSubs = displayedTotals.jobsWithoutSubs ?? 0;
  const coveragePct = totalJobs > 0 ? Math.round((jobsWithSubs / totalJobs) * 100) : 0;

  const clientRowsHtml = filteredClientBreakdown.map((c, i) => `
    <tr>
      <td style="font-weight: 600; color: #64748b;">${i + 1}</td>
      <td style="font-weight: 600; color: #0f172a;">${c.clientName}</td>
      <td style="text-align: center; font-weight: 700; color: #0f766e;">${c.jobCount}</td>
      <td style="text-align: center; font-weight: 700; color: #2563eb;">${c.submissionCount}</td>
      <td style="text-align: center;">
        ${c.submissionCount > 0 
          ? '<span class="status-tag active">✓ Active Subs</span>' 
          : '<span class="status-tag missing">⚠️ Missing Subs</span>'}
      </td>
    </tr>
  `).join('');

  const workModeList = Array.isArray(byWorkMode) 
    ? byWorkMode 
    : typeof workModeStats === 'object' && workModeStats !== null 
      ? Object.entries(workModeStats).map(([mode, count]) => ({ workMode: mode, jobCount: count }))
      : [];

  const workModeRowsHtml = workModeList.map(item => `
    <tr>
      <td style="font-weight: 600; color: #334155;">${item.workMode || 'Unspecified'}</td>
      <td style="text-align: right; font-weight: 700; color: #0f766e;">${item.jobCount || 0}</td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Job Count & Distribution Report — ${dateRange}</title>
  <style>${commonCss}</style>
</head>
<body>
  <div class="report-container">
    <div class="header">
      <div>
        <h1 class="brand-title">Prophecy <span>ERP</span> &mdash; Job Count & Distribution Report</h1>
        <p class="meta-info">Report Period: <strong>${dateRange}</strong> &nbsp;|&nbsp; Generated: <strong>${new Date().toLocaleString()}</strong></p>
      </div>
      <button class="download-pdf-btn" id="downloadPdfBtn" onclick="downloadAsPDF()">📥 Download as PDF</button>
    </div>

    <div class="filters-banner">
      <span class="filter-label">Active Filters:</span>
      <span class="badge date">📅 ${dateRange}</span>
      <span class="badge region">🌐 ${regionLabel}</span>
      <span class="badge source">⚙️ ${creationLabel}</span>
      <span class="badge client">🏢 ${clientLabel}</span>
      <span class="badge status">📊 Filter: ${subFilterLabel}</span>
    </div>

    <div class="kpi-grid">
      <div class="kpi-card teal">
        <div class="kpi-title">Total Open Jobs</div>
        <div class="kpi-value">${totalJobs}</div>
        <div class="kpi-sub">In selected period</div>
      </div>
      <div class="kpi-card blue">
        <div class="kpi-title">Total Submissions</div>
        <div class="kpi-value">${totalSubs}</div>
        <div class="kpi-sub">Submitted in period</div>
      </div>
      <div class="kpi-card green">
        <div class="kpi-title">Jobs With Subs</div>
        <div class="kpi-value">${jobsWithSubs}</div>
        <div class="kpi-sub" style="color: #059669; font-weight: 700;">${coveragePct}% Coverage</div>
      </div>
      <div class="kpi-card red">
        <div class="kpi-title">⚠️ Missing Subs</div>
        <div class="kpi-value" style="color: #dc2626;">${jobsWithoutSubs}</div>
        <div class="kpi-sub" style="color: #b91c1c; font-weight: 700;">0 Submissions</div>
      </div>
    </div>

    <div style="display: grid; grid-template-columns: 2fr 1fr; gap: 24px;">
      <div>
        <h2 class="section-title">Client Summary Breakdown (${filteredClientBreakdown.length} Clients)</h2>
        <table>
          <thead>
            <tr>
              <th style="width: 40px">#</th>
              <th>Client Name</th>
              <th style="text-align: center">Jobs</th>
              <th style="text-align: center">Submissions</th>
              <th style="text-align: center">Status</th>
            </tr>
          </thead>
          <tbody>
            ${clientRowsHtml || '<tr><td colspan="5" style="padding: 16px; text-align: center; color: #94a3b8">No client data found</td></tr>'}
          </tbody>
        </table>
      </div>

      <div>
        <h2 class="section-title">Work Mode Distribution</h2>
        <table>
          <thead>
            <tr>
              <th>Work Mode</th>
              <th style="text-align: right">Job Count</th>
            </tr>
          </thead>
          <tbody>
            ${workModeRowsHtml || '<tr><td colspan="2" style="padding: 16px; text-align: center; color: #94a3b8">No work mode data</td></tr>'}
          </tbody>
        </table>
      </div>
    </div>

    <div class="footer">
      <span>Prophecy ERP &bull; Job Analytics &bull; Confidential & Proprietary</span>
      <span>Page 1 of 1</span>
    </div>
  </div>
  ${pdfScriptTag}
</body>
</html>`;

  const fileName = `Job_Report_${regionLabel}_${creationType}_${startDate}_to_${endDate}.html`;
  triggerHtmlDownload(html, fileName);
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. CLIENT REPORT (ClientCountReport.js) HTML Generator
// ─────────────────────────────────────────────────────────────────────────────
export function generateClientReportHtml({
  startDate,
  endDate,
  region,
  creationType,
  clientFilterStatus,
  displayedTotals = {},
  clients = []
}) {
  const regionLabel = (region || '').toLowerCase() === 'offshore' ? 'Offshore' : 'Onsite';
  const creationLabel = creationType === 'automation' ? 'Automation' : creationType === 'manual' ? 'Manual' : 'All Sources';
  const subFilterLabel = clientFilterStatus === 'with_subs' ? 'With Submissions' : clientFilterStatus === 'without_subs' ? 'Without Submissions' : 'All Clients';
  const dateRange = `${startDate || 'N/A'} — ${endDate || 'N/A'}`;

  const clientRowsHtml = clients.map((c, i) => {
    const jobCount = c.jobCount ?? 0;
    const totalSourced = c.submissionCount ?? 0;
    const clientSubs = c.submittedToClientCount ?? 0;
    const ratio = jobCount > 0 ? (clientSubs / jobCount).toFixed(1) : clientSubs;

    return `
      <tr>
        <td style="font-weight: 600; color: #64748b;">${i + 1}</td>
        <td style="font-weight: 600; color: #0f172a;">${c.clientName}</td>
        <td style="text-align: center; font-weight: 700; color: #0f766e;">${jobCount}</td>
        <td style="text-align: center; font-weight: 600; color: #64748b;">${totalSourced}</td>
        <td style="text-align: center; font-weight: 700; color: #059669;">${clientSubs}</td>
        <td style="text-align: center; font-weight: 600; color: #64748b;">${ratio}</td>
        <td style="text-align: center;">
          ${clientSubs > 0 
            ? '<span class="status-tag active">✓ Active Subs</span>' 
            : '<span class="status-tag missing">⚠️ No Subs</span>'}
        </td>
      </tr>
    `;
  }).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Client Job & Submission Report — ${dateRange}</title>
  <style>${commonCss}</style>
</head>
<body>
  <div class="report-container">
    <div class="header">
      <div>
        <h1 class="brand-title">Prophecy <span>ERP</span> &mdash; Client Analytics Report</h1>
        <p class="meta-info">Report Period: <strong>${dateRange}</strong> &nbsp;|&nbsp; Generated: <strong>${new Date().toLocaleString()}</strong></p>
      </div>
      <button class="download-pdf-btn" id="downloadPdfBtn" onclick="downloadAsPDF()">📥 Download as PDF</button>
    </div>

    <div class="filters-banner">
      <span class="filter-label">Active Filters:</span>
      <span class="badge date">📅 ${dateRange}</span>
      <span class="badge region">🌐 ${regionLabel}</span>
      <span class="badge source">⚙️ ${creationLabel}</span>
      <span class="badge status">📊 Filter: ${subFilterLabel}</span>
    </div>

    <div class="kpi-grid" style="grid-template-columns: repeat(4, 1fr);">
      <div class="kpi-card teal">
        <div class="kpi-title">Total Clients</div>
        <div class="kpi-value">${displayedTotals.clientCount ?? clients.length}</div>
        <div class="kpi-sub">Active in period</div>
      </div>
      <div class="kpi-card blue">
        <div class="kpi-title">Total Jobs</div>
        <div class="kpi-value">${displayedTotals.jobCount ?? 0}</div>
        <div class="kpi-sub">Across all clients</div>
      </div>
      <div class="kpi-card yellow">
        <div class="kpi-title">Total Sourced</div>
        <div class="kpi-value">${displayedTotals.submissionCount ?? 0}</div>
        <div class="kpi-sub">All candidates sourced</div>
      </div>
      <div class="kpi-card green">
        <div class="kpi-title">Submitted to Client</div>
        <div class="kpi-value">${displayedTotals.submittedToClientCount ?? 0}</div>
        <div class="kpi-sub">Delivered to clients</div>
      </div>
    </div>

    <h2 class="section-title">Client Performance Ranking (${clients.length} Clients)</h2>
    <table>
      <thead>
        <tr>
          <th style="width: 40px">#</th>
          <th>Client Name</th>
          <th style="text-align: center">Jobs Created</th>
          <th style="text-align: center">Total Sourced</th>
          <th style="text-align: center">Submitted to Client</th>
          <th style="text-align: center">Sub / Job Ratio</th>
          <th style="text-align: center">Status</th>
        </tr>
      </thead>
      <tbody>
        ${clientRowsHtml || '<tr><td colspan="7" style="padding: 16px; text-align: center; color: #94a3b8">No clients found matching filters</td></tr>'}
      </tbody>
    </table>

    <div class="footer">
      <span>Prophecy ERP &bull; Client Performance &bull; Confidential & Proprietary</span>
      <span>Page 1 of 1</span>
    </div>
  </div>
  ${pdfScriptTag}
</body>
</html>`;

  const fileName = `Client_Report_${regionLabel}_${creationType}_${startDate}_to_${endDate}.html`;
  triggerHtmlDownload(html, fileName);
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. SUBMISSION REPORT / MANAGER DASHBOARD HTML Generator
// ─────────────────────────────────────────────────────────────────────────────
export function generateSubmissionReportHtml({
  startDate,
  endDate,
  regionFilter,
  selectedClient,
  activeFilter,
  jobStats = {},
  reportData = [],
  totalSubmissions = 0,
  topRecruiter = { name: '-', count: 0 },
  topPod = { name: '-', count: 0 }
}) {
  const regionLabel = (regionFilter || 'Onsite');
  const clientLabel = selectedClient || 'All Clients';
  const periodLabel = activeFilter ? (activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1) + ' View') : 'Custom View';
  const dateRange = `${startDate || 'N/A'} — ${endDate || 'N/A'}`;

  const totalJobs = jobStats.totalJobs ?? 0;
  const jobsWithSubs = jobStats.jobsWithSubs ?? 0;
  const jobsWithoutSubs = jobStats.jobsWithoutSubs ?? 0;
  const coveragePct = totalJobs > 0 ? Math.round((jobsWithSubs / totalJobs) * 100) : 0;

  // Flatten recruiters across teams
  let totalSourced = 0;
  let totalOutcome = 0;
  const allMembers = [];
  reportData.forEach(team => {
    if (team.members) {
      team.members.forEach(m => {
        const subCount = m.submissionCount || 0;
        const srcCount = m.sourcedCount || 0;
        const outCount = m.outcomeCount || 0;
        totalSourced += srcCount;
        totalOutcome += outCount;
        allMembers.push({
          name: m.name,
          team: team.teamName,
          submissions: subCount,
          sourced: srcCount,
          outcome: outCount,
          total: subCount + srcCount + outCount
        });
      });
    }
  });
  allMembers.sort((a, b) => b.submissions - a.submissions || b.sourced - a.sourced);

  // Recruiter leaderboard rows
  const recruiterRowsHtml = allMembers.map((m, i) => `
    <tr>
      <td style="font-weight: 700; color: ${i === 0 ? '#d97706' : i === 1 ? '#64748b' : i === 2 ? '#b91c1c' : '#374151'}">${i + 1}</td>
      <td style="font-weight: 600; color: #0f172a;">${m.name}</td>
      <td style="color: #64748b;">${m.team}</td>
      <td style="text-align: center; font-weight: 700; color: #4338ca;">${m.sourced}</td>
      <td style="text-align: right; font-weight: 700; color: #059669;">${m.submissions}</td>
    </tr>
  `).join('');

  // Team / Pod rows
  const podRowsHtml = reportData.map((team, i) => `
    <tr>
      <td style="font-weight: 600; color: #64748b;">${i + 1}</td>
      <td style="font-weight: 700; color: #0f172a;">${team.teamName}</td>
      <td style="color: #64748b;">${team.teamLead || 'N/A'}</td>
      <td style="text-align: center; font-weight: 600;">${team.memberCount || (team.members ? team.members.length : 0)}</td>
      <td style="text-align: center; font-weight: 700; color: #4338ca;">${team.totalSourced || 0}</td>
      <td style="text-align: right; font-weight: 800; color: #0f766e;">${team.totalSubmissions || 0}</td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Submission & Recruiter Performance Report — ${dateRange}</title>
  <style>${commonCss}</style>
</head>
<body>
  <div class="report-container">
    <div class="header">
      <div>
        <h1 class="brand-title">Prophecy <span>ERP</span> &mdash; Submission Performance Report</h1>
        <p class="meta-info">Report Period: <strong>${dateRange}</strong> &nbsp;|&nbsp; Generated: <strong>${new Date().toLocaleString()}</strong></p>
      </div>
      <button class="download-pdf-btn" id="downloadPdfBtn" onclick="downloadAsPDF()">📥 Download as PDF</button>
    </div>

    <div class="filters-banner">
      <span class="filter-label">Active Filters:</span>
      <span class="badge date">📅 ${dateRange}</span>
      <span class="badge region">🌐 ${regionLabel}</span>
      <span class="badge client">🏢 ${clientLabel}</span>
      <span class="badge status">⏱️ ${periodLabel}</span>
    </div>

    <div class="kpi-grid">
      <div class="kpi-card purple" style="border-left-color: #6366f1;">
        <div class="kpi-title">Total Sourced</div>
        <div class="kpi-value">${totalSourced + totalSubmissions + totalOutcome}</div>
        <div class="kpi-sub">Total Candidates Sourced</div>
      </div>
      <div class="kpi-card purple" style="border-left-color: #8b5cf6;">
        <div class="kpi-title">Pre-Client Pool</div>
        <div class="kpi-value">${totalSourced}</div>
        <div class="kpi-sub">Internal Pipeline</div>
      </div>
      <div class="kpi-card teal">
        <div class="kpi-title">Total Submissions</div>
        <div class="kpi-value">${totalSubmissions}</div>
        <div class="kpi-sub">Delivered to Clients</div>
      </div>
      <div class="kpi-card blue">
        <div class="kpi-title">Total Open Jobs</div>
        <div class="kpi-value">${totalJobs}</div>
        <div class="kpi-sub">Available positions</div>
      </div>
      <div class="kpi-card green">
        <div class="kpi-title">Jobs With Subs</div>
        <div class="kpi-value">${jobsWithSubs}</div>
        <div class="kpi-sub" style="color: #059669; font-weight: 700;">${coveragePct}% Coverage</div>
      </div>
      <div class="kpi-card red">
        <div class="kpi-title">⚠️ Missing Subs</div>
        <div class="kpi-value" style="color: #dc2626;">${jobsWithoutSubs}</div>
        <div class="kpi-sub" style="color: #b91c1c; font-weight: 700;">0 Submissions</div>
      </div>
      <div class="kpi-card amber">
        <div class="kpi-title">Top Recruiter</div>
        <div class="kpi-value" style="font-size: 18px;">${topRecruiter.name || '-'}</div>
        <div class="kpi-sub">${topRecruiter.count || topRecruiter.submissions || 0} Submissions</div>
      </div>
    </div>

    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px;">
      <div>
        <h2 class="section-title">Team / Pod Performance</h2>
        <table>
          <thead>
            <tr>
              <th style="width: 40px">#</th>
              <th>Pod Name</th>
              <th>Team Lead</th>
              <th style="text-align: center">Recruiters</th>
              <th style="text-align: center">Pre-Client Pool</th>
              <th style="text-align: right">Client Submissions</th>
            </tr>
          </thead>
          <tbody>
            ${podRowsHtml || '<tr><td colspan="6" style="padding: 16px; text-align: center; color: #94a3b8">No pod data available</td></tr>'}
          </tbody>
        </table>
      </div>

      <div>
        <h2 class="section-title">Recruiter Performance Leaderboard</h2>
        <table>
          <thead>
            <tr>
              <th style="width: 40px">#</th>
              <th>Recruiter Name</th>
              <th>Pod Name</th>
              <th style="text-align: center">Pre-Client Pool</th>
              <th style="text-align: right">Client Submissions</th>
            </tr>
          </thead>
          <tbody>
            ${recruiterRowsHtml || '<tr><td colspan="5" style="padding: 16px; text-align: center; color: #94a3b8">No recruiter data available</td></tr>'}
          </tbody>
        </table>
      </div>
    </div>

    <div class="footer">
      <span>Prophecy ERP &bull; Submission Analytics &bull; Confidential & Proprietary</span>
      <span>Page 1 of 1</span>
    </div>
  </div>
  ${pdfScriptTag}
</body>
</html>`;

  const fileName = `Submission_Performance_Report_${regionLabel}_${startDate}_to_${endDate}.html`;
  triggerHtmlDownload(html, fileName);
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. EXECUTIVE PRESENTATION DECK GENERATOR (14-Slide Reference PPTX)
// ─────────────────────────────────────────────────────────────────────────────
export function generateExecutiveDeckHtml({
  startDate,
  endDate,
  regionFilter = 'Onsite',
  selectedClient = 'All Clients',
  summary = {},
  podData = [],
  keyClosures = [],
  interviewsAwaiting = [],
  onboardingPipeline = []
}) {
  const dateRange = `${startDate || 'N/A'} — ${endDate || 'N/A'}`;
  const totalOpenJobs = Number(summary.totalOpenJobs ?? 0);
  const pods = (podData && podData.length > 0) ? podData : [];
  const podSubsSum = pods.reduce((acc, t) => acc + (Number(t.totalSubmissions) || 0), 0);
  const totalSubmissions = Number(summary.totalSubmissions ?? podSubsSum);
  const totalInterviews = Number(summary.totalInterviews ?? 0);
  const totalClosures = Number(summary.totalClosures ?? 0);

  const subCoveragePct = totalOpenJobs > 0 ? ((totalSubmissions / totalOpenJobs) * 100).toFixed(1) : '0.0';
  const interviewRatePct = totalOpenJobs > 0 ? ((totalInterviews / totalOpenJobs) * 100).toFixed(1) : '0.0';
  const closureRatePct = totalOpenJobs > 0 ? ((totalClosures / totalOpenJobs) * 100).toFixed(1) : '0.0';

  const closures = (keyClosures && keyClosures.length > 0) ? keyClosures : [];
  const activeInterviews = (interviewsAwaiting && interviewsAwaiting.length > 0) ? interviewsAwaiting : [];
  const onboarding = (onboardingPipeline && onboardingPipeline.length > 0) ? onboardingPipeline : [];

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Prophecy ERP — Executive Weekly Performance & Onboarding Report</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
      background: #f1f5f9;
      color: #0f172a;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    
    /* Top Presentation Bar */
    .top-toolbar {
      position: sticky;
      top: 0;
      z-index: 1000;
      width: 100%;
      background: #ffffff;
      border-bottom: 1px solid #e2e8f0;
      padding: 12px 28px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
    }
    .top-brand {
      font-size: 16px;
      font-weight: 800;
      color: #009688;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .top-brand span { color: #475569; font-weight: 600; font-size: 14px; }
    .top-actions {
      display: flex;
      gap: 12px;
      align-items: center;
    }
    .btn-action {
      background: #009688;
      color: #ffffff;
      border: none;
      padding: 8px 18px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
      transition: all 0.2s;
      box-shadow: 0 1px 2px rgba(0,0,0,0.05);
    }
    .btn-action:hover { background: #00796b; }
    .btn-secondary {
      background: #ffffff;
      color: #475569;
      border: 1px solid #cbd5e1;
    }
    .btn-secondary:hover { background: #f8fafc; color: #0f172a; border-color: #94a3b8; }

    /* Presentation Canvas (16:9 Widescreen Slides) */
    .deck-container {
      width: 100%;
      max-width: 1160px;
      padding: 32px 20px;
      display: flex;
      flex-direction: column;
      gap: 36px;
    }
    .slide-card {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
      padding: 36px;
      min-height: 580px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      position: relative;
      overflow: hidden;
      page-break-after: always;
    }
    .slide-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #009688, #0284c7, #8b5cf6);
    }
    .slide-header {
      border-bottom: 2px solid #009688;
      padding-bottom: 16px;
      margin-bottom: 24px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
    }
    .slide-title {
      font-size: 24px;
      font-weight: 800;
      color: #0f172a;
      letter-spacing: -0.3px;
    }
    .slide-subtitle {
      font-size: 13px;
      color: #64748b;
      margin-top: 4px;
      font-weight: 500;
    }
    .slide-number {
      font-size: 11px;
      font-weight: 700;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .slide-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    .slide-footer {
      border-top: 1px solid #f1f5f9;
      padding-top: 16px;
      margin-top: 24px;
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      color: #94a3b8;
    }

    /* Grids & Cards */
    .grid-3 {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
    }
    .grid-4 {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;
    }
    .grid-2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
    }
    .kpi-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 24px 16px;
      text-align: center;
      transition: all 0.2s;
    }
    .kpi-label {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #64748b;
      margin-bottom: 8px;
    }
    .kpi-val {
      font-size: 44px;
      font-weight: 900;
      color: #0f766e;
      line-height: 1;
      margin-bottom: 8px;
    }
    .kpi-sub {
      font-size: 12px;
      color: #64748b;
    }
    .kpi-badge {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 700;
      margin-top: 8px;
    }
    .badge-teal { background: #ccfbf1; color: #0f766e; border: 1px solid #99f6e4; }
    .badge-blue { background: #e0f2fe; color: #0284c7; border: 1px solid #bae6fd; }
    .badge-purple { background: #f3e8ff; color: #7c3aed; border: 1px solid #e9d5ff; }
    .badge-amber { background: #fef3c7; color: #d97706; border: 1px solid #fde68a; }

    /* Pod Horizontal Progress Card */
    .pod-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 14px 18px;
      margin-bottom: 12px;
    }
    .pod-header {
      display: flex;
      justify-content: space-between;
      font-weight: 700;
      font-size: 14px;
      color: #0f172a;
      margin-bottom: 8px;
    }
    .progress-bar-bg {
      background: #e2e8f0;
      border-radius: 6px;
      height: 8px;
      width: 100%;
      overflow: hidden;
    }
    .progress-bar-fill {
      height: 100%;
      border-radius: 6px;
      transition: width 0.5s ease;
    }

    /* Tables */
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
      text-align: left;
    }
    th {
      background: #f1f5f9;
      color: #334155;
      padding: 12px 14px;
      font-weight: 700;
      border-bottom: 2px solid #cbd5e1;
    }
    td {
      padding: 12px 14px;
      border-bottom: 1px solid #f1f5f9;
      color: #1e293b;
    }
    tr:last-child td { border-bottom: none; }

    /* Key Notes Box */
    .notes-box {
      background: #f8fafc;
      border-left: 4px solid #009688;
      border-radius: 0 8px 8px 0;
      padding: 16px 20px;
      margin-top: 16px;
      font-size: 13px;
      color: #334155;
      line-height: 1.6;
    }

    @media print {
      body { background: #ffffff; color: #000000; }
      .top-toolbar { display: none; }
      .deck-container { max-width: 100%; padding: 0; gap: 0; }
      .slide-card {
        border: none;
        box-shadow: none;
        background: #ffffff;
        color: #000000;
        min-height: 100vh;
        border-bottom: 2px solid #e2e8f0;
        padding: 30px 20px;
      }
    }
  </style>
</head>
<body>

  <!-- Top Action Toolbar -->
  <div class="top-toolbar">
    <div class="top-brand">
      PROPHECY ERP <span>| Executive Pipeline Deck</span>
    </div>
    <div class="top-actions">
      <button id="downloadPdfBtn" class="btn-action" onclick="downloadDeckAsPDF()">
        📥 Download as PDF
      </button>
      <button class="btn-action btn-secondary" onclick="window.print()">
        🖨️ Print / Save PDF
      </button>
    </div>
  </div>

  <div class="deck-container">

    <!-- ════════════════════ SLIDE 1: TITLE COVER ════════════════════ -->
    <div class="slide-card">
      <div class="slide-header">
        <div>
          <div class="slide-title">RECRUITMENT PIPELINE REVIEW</div>
          <div class="slide-subtitle">Team Performance — Weekly Status | Total Submissions, Client Interviews & Closures against Total Requirements</div>
        </div>
        <div class="slide-number">Slide 01 / 09</div>
      </div>
      <div class="slide-content">
        <div class="grid-4" style="margin-top: 40px;">
          <div class="kpi-card">
            <div class="kpi-label">Open Requirements</div>
            <div class="kpi-val" style="color: #0284c7;">${totalOpenJobs}</div>
            <div class="kpi-sub">Total Beeline Requirements</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Total Submissions</div>
            <div class="kpi-val" style="color: #0f766e;">${totalSubmissions}</div>
            <div class="kpi-sub">${subCoveragePct}% Sourcing Coverage</div>
            <div style="font-size: 11px; color: #0f766e; margin-top: 6px; font-weight: 600;">Submitted to Client</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Client Interviews</div>
            <div class="kpi-val" style="color: #7c3aed;">${totalInterviews}</div>
            <div class="kpi-sub">${interviewRatePct}% Conversion</div>
            <div style="font-size: 11px; color: #7c3aed; margin-top: 6px; font-weight: 600;">Client Interview 1, 2 & Final</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Total Closures</div>
            <div class="kpi-val" style="color: #d97706;">${totalClosures}</div>
            <div class="kpi-sub">${closureRatePct}% Closure Rate</div>
            <div style="font-size: 11px; color: #d97706; margin-top: 6px; font-weight: 600;">Offer &bull; Hired</div>
          </div>
        </div>
      </div>
      <div class="slide-footer">
        <span>Report Period: <strong>${dateRange}</strong> | Region: <strong>${regionFilter}</strong> | Client: <strong>${selectedClient}</strong></span>
        <span>Prophecy ERP Confidential</span>
      </div>
    </div>

    <!-- ════════════════════ SLIDE 2: PERFORMANCE AGAINST REQUIREMENTS ════════════════════ -->
    <div class="slide-card">
      <div class="slide-header">
        <div>
          <div class="slide-title">Performance Against Total Requirements</div>
          <div class="slide-subtitle">Base: <strong>${totalOpenJobs} total open requirements</strong> across clients</div>
        </div>
        <div class="slide-number">Slide 02 / 09</div>
      </div>
      <div class="slide-content">
        <div class="grid-3" style="gap: 24px;">
          <div class="kpi-card">
            <div class="kpi-label">TOTAL SUBMISSIONS</div>
            <div class="kpi-val" style="color: #0f766e;">${totalSubmissions}</div>
            <div class="kpi-badge badge-teal">${subCoveragePct}% of ${totalOpenJobs}</div>
            <div class="kpi-sub" style="margin-top: 12px; font-weight: 600; color: #334155;">Candidate sourcing pipeline</div>
            <div style="font-size: 11.5px; color: #64748b; margin-top: 4px; line-height: 1.4;">Active resumes submitted against requirements (Stage: <em>Submitted to Client</em>)</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">CLIENT INTERVIEWS</div>
            <div class="kpi-val" style="color: #7c3aed;">${totalInterviews}</div>
            <div class="kpi-badge badge-purple">${interviewRatePct}% of ${totalOpenJobs}</div>
            <div class="kpi-sub" style="margin-top: 12px; font-weight: 600; color: #7c3aed;">Client Stages Included:</div>
            <div style="font-size: 11.5px; color: #475569; margin-top: 4px; line-height: 1.4;">
              &bull; <strong>Client Interview 1</strong><br>
              &bull; <strong>Client Interview 2</strong><br>
              &bull; <strong>Client Final Round</strong>
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">TOTAL CLOSURES</div>
            <div class="kpi-val" style="color: #d97706;">${totalClosures}</div>
            <div class="kpi-badge badge-amber">${closureRatePct}% of ${totalOpenJobs}</div>
            <div class="kpi-sub" style="margin-top: 12px; font-weight: 600; color: #d97706;">Stages Included:</div>
            <div style="font-size: 11.5px; color: #475569; margin-top: 4px; line-height: 1.4;">
              &bull; <strong>Offer</strong><br>
              &bull; <strong>Hired / Joined</strong>
            </div>
          </div>
        </div>
        <div class="notes-box">
          <strong>Client Pipeline Definition:</strong> Submissions track candidate profiles submitted to the client. Interview metrics track candidates actively scheduled in <strong>Client Interview 1</strong>, <strong>Client Interview 2</strong>, or <strong>Client Final Round</strong>. Profiles at <em>Submitted to Client</em> (Stage 9) transition to Interviews once client rounds are scheduled.
        </div>
      </div>
      <div class="slide-footer">
        <span>Prophecy ERP &bull; Executive Performance Review</span>
        <span>Slide 02</span>
      </div>
    </div>

    <!-- ════════════════════ SLIDE 3: TEAM-WISE SUBMISSION SHARE ════════════════════ -->
    <div class="slide-card">
      <div class="slide-header">
        <div>
          <div class="slide-title">Team-wise Submission Share</div>
          <div class="slide-subtitle">${totalSubmissions} total submissions across ${pods.length} teams | ${subCoveragePct}% of requirements</div>
        </div>
        <div class="slide-number">Slide 03 / 09</div>
      </div>
      <div class="slide-content">
        <div style="display: flex; flex-direction: column; gap: 16px;">
          ${pods.map((p, idx) => {
            const sharePct = totalSubmissions > 0 ? ((p.totalSubmissions / totalSubmissions) * 100).toFixed(1) : '0';
            const colors = ['#009688', '#0284c7', '#8b5cf6', '#d97706', '#ec4899'];
            const color = colors[idx % colors.length];
            return `
              <div class="pod-card">
                <div class="pod-header">
                  <span>${p.teamName}</span>
                  <span style="color: ${color}; font-weight: 700;">${p.totalSubmissions} submissions &nbsp;|&nbsp; ${sharePct}%</span>
                </div>
                <div class="progress-bar-bg">
                  <div class="progress-bar-fill" style="width: ${sharePct}%; background: ${color};"></div>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>
      <div class="slide-footer">
        <span>Prophecy ERP &bull; Team Distribution</span>
        <span>Slide 03</span>
      </div>
    </div>

    <!-- ════════════════════ SLIDES 4-7: INDIVIDUAL TEAM DEEP DIVES ════════════════════ -->
    ${pods.slice(0, 4).map((team, tIdx) => `
      <div class="slide-card">
        <div class="slide-header">
          <div>
            <div class="slide-title">${team.teamName} Breakdown</div>
            <div class="slide-subtitle">Total submissions: <strong>${team.totalSubmissions || 0}</strong> across team recruiters</div>
          </div>
          <div class="slide-number">Slide 0${tIdx + 4} / 09</div>
        </div>
        <div class="slide-content">
          <div style="display: flex; flex-direction: column; gap: 14px;">
            ${(team.members || []).map((m) => {
              const mShare = (team.totalSubmissions || 0) > 0 ? ((m.submissionCount / team.totalSubmissions) * 100).toFixed(0) : 0;
              return `
                <div class="pod-card">
                  <div class="pod-header">
                    <span>${m.name}</span>
                    <span style="color: #0284c7; font-weight: 800;">${m.submissionCount} Submissions (${mShare}%)</span>
                  </div>
                  <div class="progress-bar-bg">
                    <div class="progress-bar-fill" style="width: ${mShare}%; background: #009688;"></div>
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
        <div class="slide-footer">
          <span>Prophecy ERP &bull; ${team.teamName} Detail</span>
          <span>Slide 0${tIdx + 4}</span>
        </div>
      </div>
    `).join('')}

    <!-- ════════════════════ SLIDE 8: INTERVIEWS & CLOSURES BY TEAM ════════════════════ -->
    <div class="slide-card">
      <div class="slide-header">
        <div>
          <div class="slide-title">Client Interviews & Closures by Team</div>
          <div class="slide-subtitle">${totalInterviews} active client interviews (${interviewRatePct}%) &bull; ${totalClosures} closures (${closureRatePct}%)</div>
        </div>
        <div class="slide-number">Slide 08 / 09</div>
      </div>
      <div class="slide-content">
        <div class="grid-2">
          <div>
            <h3 style="font-size: 15px; margin-bottom: 12px; color: #0284c7;">Team Comparison Table</h3>
            <table>
              <thead>
                <tr>
                  <th>Team</th>
                  <th style="text-align: center;">Subs</th>
                  <th style="text-align: center;">Client Interviews</th>
                  <th style="text-align: center;">Closures</th>
                </tr>
              </thead>
              <tbody>
                ${pods.map(p => `
                  <tr>
                    <td style="font-weight: 700; color: #0f172a;">${p.teamName}</td>
                    <td style="text-align: center;">${p.totalSubmissions || 0}</td>
                    <td style="text-align: center; color: #7c3aed; font-weight: 700;">${p.totalInterviews || 0}</td>
                    <td style="text-align: center; color: #d97706; font-weight: 700;">${p.totalClosures || 0}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <div style="margin-top: 14px; font-size: 11.5px; color: #334155; background: #f8fafc; padding: 12px 14px; border-radius: 6px; border-left: 3px solid #7c3aed; border: 1px solid #e2e8f0; line-height: 1.5;">
              <strong>🎯 Client Interview Stages Tracked:</strong> <code>Client Interview 1</code>, <code>Client Interview 2</code>, <code>Client Final Round</code>.<br>
              <span style="color: #64748b; font-size: 11px;">Candidate resumes at <em>Submitted to Client</em> (Stage 9) are counted under Submissions until the client schedules an interview.</span>
            </div>
          </div>

          <div>
            <h3 style="font-size: 15px; margin-bottom: 12px; color: #0f766e;">Key Closures & Active Interviews</h3>
            <div style="display: flex; flex-direction: column; gap: 10px; max-height: 380px; overflow-y: auto;">
              ${closures.length === 0 && activeInterviews.length === 0 ? `
                <div style="color: #64748b; font-size: 13px; padding: 24px 16px; text-align: center; background: #f8fafc; border-radius: 8px; border: 1px dashed #cbd5e1;">
                  No key closures or active interviews recorded for this period.
                </div>
              ` : ''}
              ${closures.map(c => `
                <div style="background: #f8fafc; padding: 12px 16px; border-radius: 8px; border-left: 4px solid #d97706; border: 1px solid #e2e8f0;">
                  <div style="font-weight: 700; color: #0f172a;">${c.candidateName} &bull; ${c.clientName}</div>
                  <div style="font-size: 12px; color: #64748b;">${c.jobTitle || c.roleTitle || 'Role'} &mdash; ${c.comments || 'Closure Confirmed'}</div>
                </div>
              `).join('')}
              ${activeInterviews.map(i => `
                <div style="background: #f8fafc; padding: 12px 16px; border-radius: 8px; border-left: 4px solid #7c3aed; border: 1px solid #e2e8f0;">
                  <div style="font-weight: 700; color: #0f172a;">${i.candidateName} &bull; ${i.clientName}</div>
                  <div style="font-size: 12px; color: #7c3aed; font-weight: 600;">${i.jobTitle ? i.jobTitle + ' &bull; ' : ''}${i.interviewRound || 'Interview'} &bull; ${i.feedbackStatus || 'Pending'}</div>
                </div>
              `).join('')}
            </div>
          </div>
        </div>
      </div>
      <div class="slide-footer">
        <span>Prophecy ERP &bull; Conversion Analytics</span>
        <span>Slide 08</span>
      </div>
    </div>

    <!-- ════════════════════ SLIDE 9: EXECUTIVE SUMMARY ════════════════════ -->
    <div class="slide-card">
      <div class="slide-header">
        <div>
          <div class="slide-title">Executive Summary & Strategic Insights</div>
          <div class="slide-subtitle">Key Takeaways from the ${dateRange} Performance Review</div>
        </div>
        <div class="slide-number">Slide 09 / 09</div>
      </div>
      <div class="slide-content">
        <div style="display: flex; flex-direction: column; gap: 16px;">
          <div class="notes-box" style="border-left-color: #0284c7;">
            <strong>Requirement Coverage:</strong> Against ${totalOpenJobs} total open requirements, ${totalSubmissions} submissions have been made (${subCoveragePct}% coverage), demonstrating active candidate pipeline progression.
          </div>
          <div class="notes-box" style="border-left-color: #7c3aed;">
            <strong>Interview Conversions:</strong> ${totalInterviews} interviews scheduled across teams (${interviewRatePct}% conversion of total open requirements).
          </div>
          <div class="notes-box" style="border-left-color: #d97706;">
            <strong>Closures & Offers:</strong> ${totalClosures} closures confirmed for client start dates.
          </div>
          <div class="notes-box" style="border-left-color: #e11d48;">
            <strong>Focus & Follow-ups:</strong> Monitor final round interview feedbacks and follow up on client confirmations.
          </div>
        </div>
      </div>
      <div class="slide-footer">
        <span>Prophecy ERP &bull; Strategic Recommendations</span>
        <span>Slide 09</span>
      </div>
    </div>

  </div>

  <!-- PDF Generation Script -->
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
  <script>
    function downloadDeckAsPDF() {
      var btn = document.getElementById('downloadPdfBtn');
      var originalHtml = btn ? btn.innerHTML : '';
      if (btn) {
        btn.innerHTML = '⏳ Generating PDF...';
        btn.disabled = true;
        btn.style.opacity = '0.7';
      }

      var element = document.querySelector('.deck-container') || document.body;
      var cleanTitle = 'Executive_Weekly_Report_${startDate}_to_${endDate}';

      var opt = {
        margin: [8, 8, 8, 8],
        filename: cleanTitle + '.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false, scrollY: 0 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: ['css', 'legacy'], after: '.slide-card' }
      };

      html2pdf().set(opt).from(element).save().then(function() {
        if (btn) {
          btn.innerHTML = originalHtml;
          btn.disabled = false;
          btn.style.opacity = '1';
        }
      }).catch(function(err) {
        console.error('PDF generation error:', err);
        if (btn) {
          btn.innerHTML = originalHtml;
          btn.disabled = false;
          btn.style.opacity = '1';
        }
        window.print();
      });
    }
  </script>
</body>
</html>`;

  // Open presentation deck in a new dedicated tab for direct viewing and printing
  const reportWindow = window.open('', '_blank');
  if (reportWindow) {
    reportWindow.document.write(html);
    reportWindow.document.close();
  } else {
    // Fallback to download if popups blocked
    triggerHtmlDownload(html, `Executive_Weekly_Report_${startDate}_to_${endDate}.html`);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. BENCH SUBMISSION & JOB REPORT (BenchReport.js) HTML Generator
// ─────────────────────────────────────────────────────────────────────────────
export function generateBenchReportHtml({
  startDate,
  endDate,
  selectedClient,
  granularity = 'daily',
  kpis = {},
  detailedBreakdown = [],
  clientOptions = []
}) {
  const clientLabel = selectedClient || 'All Clients';
  const periodLabel = granularity ? (granularity.charAt(0).toUpperCase() + granularity.slice(1) + ' View') : 'Daily View';
  const dateRange = `${startDate || 'N/A'} — ${endDate || 'N/A'}`;

  const totalSubmissions = kpis.totalSubmissions || 0;
  const totalOpenJobs = kpis.totalOpenJobs || 0;
  const topRecruiter = kpis.topRecruiter || { name: '—', count: 0 };
  const topClient = kpis.topClient || { name: '—', count: 0 };

  // Recruiter Rows with Progress Chevrons
  const recruiterRowsHtml = (detailedBreakdown || []).map((r, i) => {
    const rawCounts = Array.isArray(r.stepCounts) ? r.stepCounts : (typeof r.stepCounts === 'string' ? r.stepCounts.split(',').map(Number) : []);
    const counts = rawCounts.length === 10 ? rawCounts : [...Array(10)].map((_, idx) => rawCounts[idx] || 0);
    const stepsHtml = counts.map((count, idx) => {
      const hasApps = count > 0;
      return `<span style="display: inline-block; padding: 2px 5px; margin: 0 1px; border-radius: 3px; font-size: 10px; font-weight: 700; background: ${hasApps ? '#019d88' : '#f1f5f9'}; color: ${hasApps ? '#ffffff' : '#64748b'}; border: 1px solid ${hasApps ? '#008775' : '#cbd5e1'}; min-width: 14px; text-align: center;" title="Step ${idx + 1}: ${count}">${count}</span>`;
    }).join('');

    const sharePct = totalSubmissions > 0 ? Math.round((r.submissions / totalSubmissions) * 100) : 0;

    return `
      <tr>
        <td style="font-weight: 700; color: ${i === 0 ? '#d97706' : i === 1 ? '#64748b' : i === 2 ? '#b91c1c' : '#374151'}">${i + 1}</td>
        <td style="font-weight: 700; color: #0f172a;">${r.name}</td>
        <td style="color: #64748b;">${r.team || 'Bench Sales'}</td>
        <td style="text-align: center; font-weight: 800; color: #0f766e; font-size: 15px;">${r.submissions}</td>
        <td style="text-align: center;">${stepsHtml}</td>
        <td style="text-align: right; font-weight: 600; color: #3b82f6;">${sharePct}%</td>
      </tr>
    `;
  }).join('');

  // Client Breakdown Rows
  const clientRowsHtml = (clientOptions || []).map((c, i) => `
    <tr>
      <td style="font-weight: 600; color: #64748b;">${i + 1}</td>
      <td style="font-weight: 600; color: #0f172a;">${c.clientName || c.name || 'Client'}</td>
      <td style="text-align: center;">
        <span class="status-tag active">✓ Active Bench Client</span>
      </td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Bench Submission &amp; Job Report — ${dateRange}</title>
  <style>${commonCss}</style>
</head>
<body>
  <div class="report-container">
    <div class="header">
      <div>
        <h1 class="brand-title">Prophecy <span>ERP</span> &mdash; Bench Submission &amp; Job Report</h1>
        <p class="meta-info">Report Period: <strong>${dateRange}</strong> &nbsp;|&nbsp; Generated: <strong>${new Date().toLocaleString()}</strong></p>
      </div>
      <button class="download-pdf-btn" id="downloadPdfBtn" onclick="downloadAsPDF()">📥 Download as PDF</button>
    </div>

    <div class="filters-banner">
      <span class="filter-label">Active Filters:</span>
      <span class="badge date">📅 ${dateRange}</span>
      <span class="badge client">🏢 ${clientLabel}</span>
      <span class="badge status">📊 View: ${periodLabel}</span>
    </div>

    <div class="kpi-grid">
      <div class="kpi-card purple" style="border-left-color: #6366f1;">
        <div class="kpi-title">Total Sourced</div>
        <div class="kpi-value">${(kpis.totalSourced || 0) + totalSubmissions}</div>
        <div class="kpi-sub">Total Candidates Sourced</div>
      </div>
      <div class="kpi-card purple" style="border-left-color: #8b5cf6;">
        <div class="kpi-title">Pre-Client Pool</div>
        <div class="kpi-value">${kpis.totalSourced || 0}</div>
        <div class="kpi-sub">Internal Pipeline</div>
      </div>
      <div class="kpi-card teal">
        <div class="kpi-title">Total Submissions</div>
        <div class="kpi-value">${totalSubmissions}</div>
        <div class="kpi-sub">Bench profiles submitted</div>
      </div>
      <div class="kpi-card blue">
        <div class="kpi-title">Total Open Jobs</div>
        <div class="kpi-value">${totalOpenJobs}</div>
        <div class="kpi-sub">Bench jobs in period</div>
      </div>
      <div class="kpi-card purple">
        <div class="kpi-title">Top Recruiter</div>
        <div class="kpi-value" style="font-size: 20px; font-weight: 800; color: #6b21a8; line-height: 1.2;">${topRecruiter.name}</div>
        <div class="kpi-sub" style="margin-top: 4px;"><strong>${topRecruiter.count}</strong> submissions</div>
      </div>
      <div class="kpi-card amber">
        <div class="kpi-title">Top Client</div>
        <div class="kpi-value" style="font-size: 20px; font-weight: 800; color: #b45309; line-height: 1.2;">${topClient.name}</div>
        <div class="kpi-sub" style="margin-top: 4px;"><strong>${topClient.count}</strong> submissions</div>
      </div>
    </div>

    <div style="margin-bottom: 32px;">
      <h2 class="section-title" style="margin-bottom: 12px; font-size: 16px; font-weight: 700; color: #0f172a;">
        Recruiter Performance Leaderboard &amp; Technical Pipeline Progress
      </h2>
      <table>
        <thead>
          <tr>
            <th style="width: 40px;">#</th>
            <th>Recruiter Name</th>
            <th>Team Pod</th>
            <th style="text-align: center;">Submissions</th>
            <th style="text-align: center;">10-Stage Technical Pipeline Progress</th>
            <th style="text-align: right;">Share (%)</th>
          </tr>
        </thead>
        <tbody>
          ${recruiterRowsHtml || '<tr><td colspan="6" style="padding: 16px; text-align: center; color: #94a3b8">No bench submissions data found for this period</td></tr>'}
        </tbody>
      </table>
    </div>

    ${clientOptions && clientOptions.length > 0 ? `
      <div style="margin-bottom: 24px;">
        <h2 class="section-title" style="margin-bottom: 12px; font-size: 15px; font-weight: 700; color: #0f172a;">
          Active Bench Clients (${clientOptions.length} Clients)
        </h2>
        <table>
          <thead>
            <tr>
              <th style="width: 40px;">#</th>
              <th>Client Name</th>
              <th style="text-align: center;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${clientRowsHtml}
          </tbody>
        </table>
      </div>
    ` : ''}

    <div class="footer" style="margin-top: 24px; padding-top: 14px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 12px; color: #64748b;">
      <span>Prophecy ERP &bull; Bench Job Analytics &bull; Confidential & Proprietary</span>
      <span>Page 1 of 1</span>
    </div>
  </div>
  ${pdfScriptTag}
</body>
</html>`;

  const fileName = `Bench_Report_${selectedClient || 'AllClients'}_${startDate}_to_${endDate}.html`;
  
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  } else {
    triggerHtmlDownload(html, fileName);
  }
}


